import { forceCenter, forceCollide, forceLink, forceManyBody, forceSimulation, type SimulationNodeDatum } from "d3-force-3d"
import type { EntityGraph, GraphNode } from "@/lib/entity-graph"

export interface Positioned {
  x: number
  y: number
}

export type Layout = Map<string, Positioned>

export interface Extent {
  minX: number
  minY: number
  maxX: number
  maxY: number
}

interface LayoutNode extends SimulationNodeDatum {
  id: string
  r: number
}

/** Deterministic pseudo-random so the map settles the same way every time it opens */
function mulberry32(seed: number): () => number {
  let a = seed >>> 0
  return () => {
    a = (a + 0x6d2b79f5) >>> 0
    let t = a
    t = Math.imul(t ^ (t >>> 15), t | 1)
    t ^= t + Math.imul(t ^ (t >>> 7), t | 61)
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296
  }
}

/** Circle radius in px: bigger entities (more fields) read larger */
export function nodeRadius(node: GraphNode): number {
  return 6 + Math.sqrt(node.fieldCount ?? 30) * 0.9
}

const GOLDEN_ANGLE = Math.PI * (3 - Math.sqrt(5))

/**
 * 2D force layout of the entity graph (d3-force-3d run in two dimensions): connected
 * entities pull together, everything repels, the graph is centred. Entities without
 * any known association start on outer rings by group so they read as a catalog rather
 * than noise. Runs to convergence synchronously in px units. Nodes already placed in
 * `previous` start from there, so the map settles rather than jumps as schemas arrive.
 */
export function layoutEntityGraph(graph: EntityGraph, seed = 7, previous?: Layout): Layout {
  const random = mulberry32(seed)
  const connected = new Set(graph.edges.flatMap((e) => [e.from, e.to]))
  const groups = Array.from(new Set(graph.nodes.map((n) => n.group)))

  const nodes: LayoutNode[] = graph.nodes.map((node, index) => {
    const known = previous?.get(node.id)
    if (known) return { id: node.id, r: nodeRadius(node), x: known.x, y: known.y }
    // Sunflower spiral: an even start that the forces then refine
    const angle = index * GOLDEN_ANGLE
    const ring = connected.has(node.id) ? 40 + Math.sqrt(index) * 14 : 300 + groups.indexOf(node.group) * 30 + Math.sqrt(index) * 3
    return { id: node.id, r: nodeRadius(node), x: ring * Math.cos(angle), y: ring * Math.sin(angle) }
  })

  const links = graph.edges.map((edge) => ({ source: edge.from, target: edge.to }))

  const simulation = forceSimulation<LayoutNode>(nodes, 2)
    .randomSource(random)
    .force(
      "link",
      forceLink<LayoutNode, { source: string | LayoutNode; target: string | LayoutNode }>(links)
        .id((n) => n.id)
        .distance(90)
        .strength(0.5)
    )
    .force("charge", forceManyBody<LayoutNode>().strength(-140).distanceMax(420))
    .force("collide", forceCollide<LayoutNode>((n) => n.r + 8))
    .force("center", forceCenter<LayoutNode>(0, 0))
    .alphaDecay(0.035)
    .stop()

  simulation.tick(previous && previous.size > 0 ? 90 : 200)

  return new Map(nodes.map((n) => [n.id, { x: n.x ?? 0, y: n.y ?? 0 }]))
}

/** Bounding box of the given nodes (every node by default), for fitting the view */
export function layoutExtent(layout: Layout, ids?: Iterable<string>, padding = 40): Extent {
  const extent: Extent = { minX: Infinity, minY: Infinity, maxX: -Infinity, maxY: -Infinity }
  const points = ids ? Array.from(ids, (id) => layout.get(id)).filter((p): p is Positioned => !!p) : Array.from(layout.values())
  for (const p of points) {
    extent.minX = Math.min(extent.minX, p.x)
    extent.minY = Math.min(extent.minY, p.y)
    extent.maxX = Math.max(extent.maxX, p.x)
    extent.maxY = Math.max(extent.maxY, p.y)
  }
  if (!Number.isFinite(extent.minX)) return { minX: -padding, minY: -padding, maxX: padding, maxY: padding }
  return { minX: extent.minX - padding, minY: extent.minY - padding, maxX: extent.maxX + padding, maxY: extent.maxY + padding }
}

export interface Viewport {
  /** Translation in px */
  x: number
  y: number
  /** Scale */
  k: number
}

/** The viewport that shows the whole extent inside a width × height area */
export function fitViewport(extent: Extent, width: number, height: number): Viewport {
  const w = Math.max(1, extent.maxX - extent.minX)
  const h = Math.max(1, extent.maxY - extent.minY)
  const k = Math.min(2, Math.max(0.1, Math.min(width / w, height / h)))
  return { k, x: width / 2 - ((extent.minX + extent.maxX) / 2) * k, y: height / 2 - ((extent.minY + extent.maxY) / 2) * k }
}

/** The viewport that puts `point` in the centre at the current scale */
export function centreViewport(view: Viewport, point: Positioned, width: number, height: number): Viewport {
  return { k: view.k, x: width / 2 - point.x * view.k, y: height / 2 - point.y * view.k }
}

/** Zooms by `factor` around a screen point so what is under the cursor stays put */
export function zoomViewport(view: Viewport, factor: number, at: Positioned): Viewport {
  const k = Math.min(6, Math.max(0.08, view.k * factor))
  const scale = k / view.k
  return { k, x: at.x - (at.x - view.x) * scale, y: at.y - (at.y - view.y) * scale }
}
